import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useI18n } from "../i18n/I18nContext";
import { useSEO } from "../lib/seo";
import { MessageCircle, Phone, Clock, MapPin } from "lucide-react";
import { WHATSAPP_HREF, PHONE_HREF, WHATSAPP_LOCAL } from "../components/WhatsAppFab";

export default function Contact() {
  const { locale, t } = useI18n();
  useSEO({
    title: locale === "fr" ? "Contact · SAFEMAT" : "Kontakt · SAFEMAT",
    description: locale === "fr"
      ? "Contactez SAFEMAT par WhatsApp ou téléphone. Réponse rapide pour valider votre surface tatami et planifier."
      : "Kontaktieren Sie SAFEMAT per WhatsApp oder Telefon. Schnelle Antwort zur Validierung Ihrer Tatami-Fläche.",
    locale,
  });

  const copy = locale === "fr" ? {
    eyebrow: "Contact",
    title: "Parlons directement.",
    lead: "Pour valider votre surface tatami, planifier une intervention ou poser une question, contactez-nous directement par WhatsApp ou par téléphone. Réponse rapide, sans formulaire intermédiaire.",
    whyTitle: "Pourquoi pas de formulaire ?",
    whyLead: "Nous préférons une vraie conversation. Vous obtenez une réponse précise plus vite, et nous évaluons mieux votre besoin réel.",
    whatsAppCta: "Écrire sur WhatsApp",
    callCta: "Appeler maintenant",
    hoursLabel: "Disponibilité",
    hours: "Lundi – Vendredi · 9 h – 19 h\nSamedi · 10 h – 16 h",
    regionLabel: "Région",
    region: "Suisse romande & alémanique",
    note: "Les interventions sont planifiées exclusivement en dehors des heures de cours du club.",
  } : {
    eyebrow: "Kontakt",
    title: "Sprechen wir direkt.",
    lead: "Um Ihre Tatami-Fläche zu validieren, einen Einsatz zu planen oder eine Frage zu stellen, kontaktieren Sie uns direkt per WhatsApp oder Telefon. Schnelle Antwort, ohne Zwischenformular.",
    whyTitle: "Warum kein Formular?",
    whyLead: "Wir bevorzugen ein echtes Gespräch. Sie erhalten schneller eine präzise Antwort, und wir verstehen Ihren Bedarf besser.",
    whatsAppCta: "WhatsApp schreiben",
    callCta: "Jetzt anrufen",
    hoursLabel: "Verfügbarkeit",
    hours: "Montag – Freitag · 9 – 19 Uhr\nSamstag · 10 – 16 Uhr",
    regionLabel: "Region",
    region: "Romandie & Deutschschweiz",
    note: "Einsätze werden ausschliesslich ausserhalb der Trainingszeiten des Clubs geplant.",
  };

  return (
    <div data-testid="contact-page">
      <Header />
      <div className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-24 pb-12 lg:pt-32 lg:pb-16">
          <p className="eyebrow text-slate-400 mb-4">{copy.eyebrow}</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] max-w-3xl">
            {copy.title}
          </h1>
          <p className="mt-6 text-slate-300 max-w-2xl leading-relaxed">{copy.lead}</p>
        </div>
      </div>

      <main className="bg-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 section grid lg:grid-cols-12 gap-12 items-start">
          {/* Left: CTAs */}
          <div className="lg:col-span-7">
            <span className="accent-bar" />
            <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">{WHATSAPP_LOCAL}</h2>
            <p className="mt-3 text-slate-600">{copy.note}</p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={WHATSAPP_HREF}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold rounded text-sm transition-colors"
                data-testid="contact-whatsapp-btn"
              >
                <MessageCircle size={18} strokeWidth={2.2} />
                {copy.whatsAppCta}
              </a>
              <a
                href={PHONE_HREF}
                className="btn-primary"
                data-testid="contact-call-btn"
              >
                <Phone size={16} />
                {copy.callCta}
              </a>
            </div>

            <div className="mt-12 border-t border-slate-200 pt-8">
              <h3 className="font-display font-semibold text-lg">{copy.whyTitle}</h3>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed max-w-xl">{copy.whyLead}</p>
            </div>
          </div>

          {/* Right: side info */}
          <aside className="lg:col-span-5 space-y-5">
            <div className="card p-6">
              <div className="flex items-start gap-3">
                <Clock size={18} className="text-[#016C36] mt-0.5 shrink-0" />
                <div>
                  <p className="eyebrow mb-2">{copy.hoursLabel}</p>
                  <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{copy.hours}</p>
                </div>
              </div>
            </div>
            <div className="card p-6">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-[#016C36] mt-0.5 shrink-0" />
                <div>
                  <p className="eyebrow mb-2">{copy.regionLabel}</p>
                  <p className="text-sm text-slate-700 leading-relaxed">{copy.region}</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
